import type {List} from 'immutable';

import sessionStore from 'client/Session/SessionStore';
import {useAdminContentLocks} from 'client/components/ContentDiscovery/ManageRestrictionsModal/useAdminContentLocks';
import {
  getQuestionSetGuideLevelId,
  getQuestionSetGuideLevelIds
} from 'client/components/ContentDiscovery/ManageRestrictionsModal/restrictionIndicator.utils';
import {isQuestionSetRestrictedForTeacher} from 'client/components/TeacherRestrictedContent';
import type {QuestionSetModelV1} from 'src/resources/augmented/QuestionSet/QuestionSetModel.v1';

/**
 * Whether the given assignment question sets contain restricted questions — real
 * detection, so it works pre-save. Pass the builder's selected question sets when
 * creating, or the loaded assignment's question sets (`assignment.getQuestionSets()`)
 * when viewing/editing an existing assignment — both carry guide levels + questions.
 *
 * "Restricted" is viewer-relative, so detection is role-aware:
 *   - **admin** → a question set's guide level is locked at one of the admin's
 *     schools (`useAdminContentLocks`). Admins are never blocked, so a question's
 *     per-viewer `isContentLocked` flag is false for them; the lock state has to
 *     come from the admin's school lock records.
 *   - **teacher** → a question set is content-locked for them (the backend
 *     redacts it), via `isQuestionSetRestrictedForTeacher`.
 *
 * `enabled` lets the caller gate the whole thing on a feature flag — when false
 * the admin content-locks fetch is skipped and detection returns false.
 */
export function useAssignmentHasRestrictedContent(
  questionSets: List<QuestionSetModelV1> | null | undefined,
  enabled = true
): boolean {
  const isAdmin = sessionStore.isSchoolAdministrator();
  const {getBreakdown, loading} = useAdminContentLocks({enabled: enabled && isAdmin});

  if (!enabled || !questionSets || questionSets.size === 0) {
    return false;
  }

  if (isAdmin) {
    if (loading) {
      return false;
    }
    return questionSets.some((questionSet) => {
      const ids = [
        getQuestionSetGuideLevelId(questionSet),
        ...getQuestionSetGuideLevelIds(questionSet)
      ].filter((id): id is string => Boolean(id));
      return ids.length > 0 && getBreakdown(ids).restricted.length > 0;
    });
  }

  return questionSets.some((questionSet) => isQuestionSetRestrictedForTeacher(questionSet));
}
